import React from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import { reportBudget } from 'loot-core/client/queries';
import * as monthUtils from 'loot-core/shared/months';
import { amountToCurrency, integerToAmount } from 'loot-core/shared/util';

import { chartTooltipStyle } from '../../../budget-template/reports/shared';
import { View } from '../../../common/View';
import { useFormat } from '../../../spreadsheet/useFormat';
import { useSheetValue } from '../../../spreadsheet/useSheetValue';

type BudgetSummaryProps = {
  month?: string;
  data?: any;
};

function CustomTooltip({ payload }: any) {
  if (!payload || !payload[0]) {
    return null;
  }
  const data = payload[0].payload;

  return (
    <View style={chartTooltipStyle}>
      <span>Name: {data.name}</span>
      <br />
      <span style={{ color: '#4285F4' }}>
        Budget: {amountToCurrency(data.budget)}
      </span>
      <br />
      <span style={{ color: '#DC8B06' }}>
        Actual: {amountToCurrency(data.actual)}
      </span>
    </View>
  );
}

export function BudgetSummaryChart({
  data,
  month = monthUtils.currentMonth(),
}: BudgetSummaryProps) {
  const format = useFormat();

  const totalIncome = useSheetValue(
    monthUtils.sheetForMonth(month) + '!' + reportBudget.totalIncome,
  );
  const totalBudgetedIncome = useSheetValue(
    monthUtils.sheetForMonth(month) + '!' + reportBudget.totalBudgetedIncome,
  );
  // Reverse total spent, and also set a bottom boundary of 0 (in case
  // income goes into an expense category and it's "positive", don't
  // show that in the graph)
  const totalSpent = Math.max(
    -useSheetValue(
      monthUtils.sheetForMonth(month) + '!' + reportBudget.totalSpent,
    ),
    0,
  );
  const totalBudgetedExpense = useSheetValue(
    monthUtils.sheetForMonth(month) + '!' + reportBudget.totalBudgetedExpense,
  );
  const totalSaved = useSheetValue(
    monthUtils.sheetForMonth(month) + '!' + reportBudget.totalSaved,
  );
  const totalBudgetedSaved = useSheetValue(
    monthUtils.sheetForMonth(month) + '!' + reportBudget.totalBudgetedSaved,
  );

  const defaultData = [
    {
      name: 'Income',
      budget: totalBudgetedIncome && integerToAmount(totalBudgetedIncome),
      actual: totalIncome && integerToAmount(totalIncome),
    },
    {
      name: 'Expenses',
      budget: totalBudgetedExpense && integerToAmount(totalBudgetedExpense),
      actual: totalSpent && integerToAmount(totalSpent),
    },
    {
      name: 'Net',
      budget: totalBudgetedSaved && integerToAmount(totalBudgetedSaved),
      actual: totalSaved && integerToAmount(totalSaved),
    },
  ];

  const legendPayload = [
    {
      name: 'Budget',
      key: 'budget',
      color: '#4285F4',
    },
    {
      name: 'Actual',
      key: 'actual',
      color: '#DC8B06',
    },
  ];

  return (
    <View data-adoption-id={`report-responsive-chart-${month}`}>
      <ResponsiveContainer
        height={200}
        style={{
          display: 'flex',
          paddingTop: 12,
          paddingBottom: 12,
        }}
      >
        <BarChart layout="vertical" data={data || defaultData} barSize={15}>
          <CartesianGrid strokeDasharray="3 3" />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            payload={legendPayload.map(p => ({
              value: p.name,
              type: 'rect',
              color: p.color,
            }))}
          />
          <XAxis type="number" padding={{ left: 60, right: 60 }} />
          <YAxis type="category" dataKey="name" />
          <ReferenceLine x={0} stroke="#000" />
          <TooltipRecharts content={<CustomTooltip />} />
          {legendPayload.map(p => (
            <Bar
              key={p.name}
              dataKey={p.key}
              fill={p.color}
              label={{
                position: 'right',
                formatter: value =>
                  format(Math.floor(value * 100), 'financial'),
                fill: p.color,
              }}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </View>
  );
}
